/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@nordcloud/gnui";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { ApplicationPDService } from "~/models";
import { PDEscalationPolicy, PDService } from "~/models/pagerDuty";
import { ACTIONS, Can } from "~/routing";

type Props = {
  addPDService: (data: ApplicationPDService) => Promise<void>;
  services: PDService[];
  escalationPolicies: PDEscalationPolicy[];
};

function PDServiceAdd({ addPDService, services, escalationPolicies }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const methods = useForm();
  const watcher = methods.watch();

  const onSubmit = async (values: ApplicationPDService) => {
    setIsLoading(true);
    values.softLaunchEpId = values.softLaunchEpId || null;
    try {
      await addPDService(values);
    } finally {
      setIsLoading(false);
      setIsSidebarOpen(false);
    }
  };

  const serviceOptions = services.map((s) => ({
    value: s.pagerduty_id,
    label: s.name,
  }));
  const epOptions = escalationPolicies.map((ep) => ({
    value: ep.pagerduty_id,
    label: ep.name,
  }));

  return (
    <>
      <Can action={ACTIONS.EDIT_APPLICATION}>
        <Button
          size="md"
          css={{ marginLeft: "auto", marginBottom: "1rem" }}
          icon="plus"
          onClick={() => setIsSidebarOpen(true)}
        >
          Add service
        </Button>
      </Can>

      <SidebarForm
        title="Add PagerDuty service"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarDropdown
          name="serviceId"
          label="Service"
          options={serviceOptions}
          required
        />
        <SidebarDropdown
          name="softLaunchEpId"
          label="Soft launch escalation policy"
          options={epOptions}
        />
        <SidebarDropdown
          name="targetEpId"
          label="Target escalation policy"
          options={epOptions}
          required
        />

        <SidebarFormButtons
          submitText="Add"
          submitEnabled={!!(watcher.serviceId && watcher.targetEpId)}
          submitLoading={isLoading}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { PDServiceAdd };
